import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUnsubscribeCandidates, unsubscribe, checkOpenClawAccess, getBrowserTaskStatus, cancelBrowserTask, type UnsubscribeCandidate } from '@/api/messages';
import { pollUntil } from '@/lib/poll';
import { ApiError } from '@/api/client';
import { MailX, X, Loader2, CheckCircle2, AlertCircle, Brain, WifiOff, ExternalLink } from 'lucide-react';

interface MassUnsubscribeModalProps {
  mailboxId: string;
  mailboxEmail: string;
  folder?: string;
  spamAction?: 'clean' | 'review' | 'spam';
  onClose: () => void;
}

type ItemStatus = 'pending' | 'processing' | 'success' | 'failed';

interface ItemState {
  status: ItemStatus;
  error?: string;
  result?: { type: string; manualFollowUrl?: string; browserTaskId?: string };
}

// How long to wait for the browser agent on one sender before giving up (B-20).
const BROWSER_TASK_TIMEOUT_MS = 10 * 60 * 1000;
// UIDs are only unique per folder, so statuses are keyed by folder and uid.
const itemKey = (c: UnsubscribeCandidate) => `${c.folder || ''}:${c.uid}`;

export function MassUnsubscribeModal({ mailboxId, mailboxEmail, folder, spamAction, onClose }: MassUnsubscribeModalProps) {
  const queryClient = useQueryClient();
  const [statuses, setStatuses] = useState<Record<string, ItemState>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const cancelRef = useRef(false);

  const llmCheck = useQuery({
    queryKey: ['openclaw-access'],
    queryFn: checkOpenClawAccess,
    staleTime: 30_000,
  });

  const candidatesQuery = useQuery({
    queryKey: ['unsubscribe-candidates', mailboxId, folder, spamAction],
    queryFn: () => getUnsubscribeCandidates(mailboxId, folder, spamAction),
  });

  const candidates = candidatesQuery.data?.candidates || [];
  const truncated = candidatesQuery.data?.truncated || false;
  const llm = llmCheck.data;
  const gatewayUp = llm?.gateway;

  // Browser-task pollers, stopped when the modal closes (B-20: they used to run forever).
  const pollers = useRef(new Map<string, () => void>());
  const unmounted = useRef(false);
  useEffect(() => () => { unmounted.current = true; pollers.current.forEach((stop) => stop()); pollers.current.clear(); }, []);

  // Poll for browser task completion (used when page needs interaction)
  const pollBrowserTask = (key: string, taskId: string, manualFollowUrl?: string) => {
    pollers.current.get(key)?.();
    if (unmounted.current) return; // closed while the request was in flight
    const fail = (error: string) => setStatuses((prev) => ({ ...prev, [key]: { status: 'failed', error, result: { type: 'http', manualFollowUrl, browserTaskId: taskId } } }));
    const stop = pollUntil(async () => {
      const st = await getBrowserTaskStatus(taskId).catch((err) => {
        if (err instanceof ApiError && err.status === 404) return null; // gone: nothing left to wait for
        throw err;
      });
      if (!st) { fail('The browser task is no longer available'); return true; }
      if (st.status === 'completed') {
        if (st.result?.success) setStatuses((prev) => ({ ...prev, [key]: { status: 'success', result: { type: 'http', browserTaskId: taskId } } }));
        else fail(st.result?.message || st.error || 'Browser agent could not complete');
        return true;
      }
      if (st.status === 'failed') { fail(st.error || 'Browser agent failed'); return true; }
      if (st.status === 'cancelled') { fail('Browser task was cancelled'); return true; }
      return false;
    }, {
      intervalMs: 3000,
      timeoutMs: BROWSER_TASK_TIMEOUT_MS,
      onTimeout: () => {
        cancelBrowserTask(taskId).catch(() => {});
        fail('The browser agent did not finish within 10 minutes');
      },
    });
    pollers.current.set(key, stop);
  };

  const handleResult = (key: string, res: any) => {
    const browserTaskId = res.result?.browserTaskId;
    const manualFollowUrl = res.result?.manualFollowUrl;
    // A browser hand-off is still in progress, whether the first page answered 200 or 4xx.
    if (browserTaskId) {
      setStatuses((prev) => ({ ...prev, [key]: { status: 'processing', result: { type: 'http', browserTaskId } } }));
      pollBrowserTask(key, browserTaskId, manualFollowUrl);
    } else if (res.ok) {
      setStatuses((prev) => ({ ...prev, [key]: { status: 'success', result: { type: res.followed?.type || 'http' } } }));
    } else {
      setStatuses((prev) => ({ ...prev, [key]: { status: 'failed', error: res.error || res.result?.error || 'Failed' } }));
    }
  };

  // B-18: each candidate carries its folder, so the right message's links are followed.
  const runOne = async (c: UnsubscribeCandidate) => {
    const key = itemKey(c);
    setStatuses((prev) => ({ ...prev, [key]: { status: 'processing' } }));
    try {
      handleResult(key, await unsubscribe(mailboxId, c.uid, c.folder));
    } catch (err: any) {
      setStatuses((prev) => ({ ...prev, [key]: { status: 'failed', error: err.message } }));
    }
  };

  const processOne = async (c: UnsubscribeCandidate) => {
    await runOne(c);
    queryClient.invalidateQueries({ queryKey: ['messages'] });
  };

  const processAll = async () => {
    cancelRef.current = false;
    setIsProcessing(true);
    for (const c of candidates) {
      if (cancelRef.current) break;
      await runOne(c);
    }
    setIsProcessing(false);
    queryClient.invalidateQueries({ queryKey: ['messages'] });
  };

  const cancelProcessing = () => { cancelRef.current = true; };

  const successCount = Object.values(statuses).filter((s) => s.status === 'success').length;
  const failedCount = Object.values(statuses).filter((s) => s.status === 'failed').length;
  const processedCount = successCount + failedCount;
  const needsManualCount = Object.values(statuses).filter((s) => s.result?.manualFollowUrl).length;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget && !isProcessing) onClose(); }}>
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl border border-bmail-border bg-bmail-surface shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-bmail-border p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-bmail-text">
            <MailX size={20} /> Mass Unsubscribe
            <span className="text-sm font-normal text-bmail-text-secondary">— {mailboxEmail}</span>
          </h2>
          {!isProcessing && (
            <button onClick={onClose} className="text-bmail-text-secondary hover:text-bmail-text">
              <X size={20} />
            </button>
          )}
        </div>

        {/* Gateway check */}
        <div className="border-b border-bmail-border px-6 py-3">
          <div className="flex items-center gap-2 text-sm">
            {/* While this loads, the connection pill is the loading indicator. */}
            {llm && gatewayUp && <><Brain size={14} className="text-green-500" /> <span className="text-bmail-text">OpenClaw gateway available — browser escalation ready</span></>}
            {llm && !gatewayUp && <><WifiOff size={14} className="text-bmail-warning" /> <span className="text-bmail-warning">OpenClaw gateway unreachable</span></>}
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {candidatesQuery.isLoading && (
            <div className="flex items-center justify-center py-8 text-bmail-text-secondary">
              Scanning inbox for subscription emails…
            </div>
          )}

          {candidatesQuery.isError && (
            <div className="flex items-center justify-center py-8 text-bmail-error">
              <AlertCircle size={18} className="mr-2" /> Failed to scan inbox
            </div>
          )}

          {candidates.length === 0 && !candidatesQuery.isLoading && (
            <div className="py-8 text-center text-bmail-text-secondary">
              <MailX size={32} className="mx-auto mb-2 opacity-50" />
              No subscription emails found in your inbox.
            </div>
          )}

          {candidates.length > 0 && (
            <>
              {truncated && (
                <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                  Scan limit reached: showing only the newest 500 messages in this scope. Some older senders may not be listed.
                </div>
              )}
              <div className="mb-4 text-sm text-bmail-text-secondary">
                Found <span className="font-medium text-bmail-text">{candidates.length}</span> sender{candidates.length !== 1 ? 's' : ''} with unsubscribe links.
                {processedCount > 0 && ` ${processedCount} processed — ${successCount} success, ${failedCount} failed.`}
                {needsManualCount > 0 && ` ${needsManualCount} need manual confirmation.`}
              </div>

              {/* Candidate list */}
              <div className="space-y-2">
                {candidates.map((c) => {
                  const st = statuses[itemKey(c)];
                  return (
                    <div key={itemKey(c)} className="rounded-lg border border-bmail-border bg-bmail-navy/20">
                      <div className="flex items-center gap-3 p-3">
                        {/* Status icon */}
                        <div className="shrink-0">
                          {!st && <div className="h-5 w-5 rounded-full border-2 border-bmail-border" />}
                          {st?.status === 'processing' && <Loader2 size={20} className="animate-spin text-bmail-gold" />}
                          {st?.status === 'success' && <CheckCircle2 size={20} className="text-green-500" />}
                          {st?.status === 'failed' && <AlertCircle size={20} className="text-bmail-error" />}
                        </div>

                        {/* Sender info */}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-bmail-text">
                            {c.fromName || c.fromEmail}
                          </div>
                          <div className="truncate text-xs text-bmail-text-secondary">
                            {c.fromEmail} · {c.linkType === 'mailto' ? 'email unsubscribe' : `${c.linkCount} link${c.linkCount !== 1 ? 's' : ''}`}
                          </div>
                          {st?.error && <div className="mt-1 text-xs text-bmail-error">{st.error}</div>}
                          {st?.result?.manualFollowUrl && (
                            <a href={st.result.manualFollowUrl} target="_blank" rel="noopener noreferrer" className="mt-1 flex items-center gap-1 text-xs text-bmail-gold hover:underline break-all">
                              <ExternalLink size={10} className="shrink-0" /> Needs manual confirmation — open link
                            </a>
                          )}
                          {st?.result?.browserTaskId && st.status === 'processing' && (
                            <div className="mt-1 text-xs text-bmail-gold">Browser agent working…</div>
                          )}
                        </div>

                        {/* Action button */}
                        <button
                          onClick={() => processOne(c)}
                          disabled={isProcessing || st?.status === 'processing' || st?.status === 'success'}
                          className="shrink-0 rounded-lg border border-bmail-border px-3 py-1.5 text-xs font-medium text-bmail-text hover:bg-bmail-surface-hover disabled:opacity-40"
                        >
                          {st?.status === 'success' ? 'Done' : st?.status === 'failed' ? 'Retry' : 'Unsubscribe'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-bmail-border p-6">
          <div className="text-sm text-bmail-text-secondary">
            {isProcessing ? `Processing… ${processedCount}/${candidates.length}` : ''}
          </div>
          <div className="flex gap-2">
            {candidates.length > 0 && !isProcessing && processedCount === 0 && (
              <button
                onClick={processAll}
                className="rounded-lg bg-bmail-goldfill px-4 py-2 text-sm font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover"
              >
                Unsubscribe All
              </button>
            )}
            {isProcessing && (
              <button
                onClick={cancelProcessing}
                className="rounded-lg border border-bmail-border px-4 py-2 text-sm text-bmail-text hover:bg-bmail-surface-hover"
              >
                Cancel
              </button>
            )}
            <button onClick={onClose} disabled={isProcessing} className="rounded-lg border border-bmail-border px-4 py-2 text-sm text-bmail-text hover:bg-bmail-surface-hover disabled:opacity-40">
              {isProcessing ? 'Processing…' : processedCount > 0 ? 'Done' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}